import { ref, inject, computed, watch, onMounted } from 'vue';
import debounce from 'lodash/debounce';
import ProfileLinkWrapper from '../component-lib.esm4.js';
import ProfileTitle from '../title/component-lib.esm.js';
import { useLink } from '../component-lib.esm6.js';
import GridLoadingItem from './components/component-lib.esm2.js';
import GridExpanderLoader from './components/component-lib.esm3.js';
import { LINK_PROPS } from '../component-lib.esm3.js';
import { useConfiguration } from '../../compositions/component-lib.esm26.js';
import { localAxios } from '../../utils/component-lib.esm15.js';

//
const PAGE_SIZE = 9;
var script = {
  components: {
    ProfileLinkWrapper,
    GridItem: () => ({
      component: import( /* webpackPrefetch: true */'./components/component-lib.esm4.js').then(d => d.default),
      loading: GridLoadingItem
    }),
    GridItemExpander: () => ({
      component: import( /* webpackPrefetch: true */'./components/component-lib.esm.js').then(d => d.default),
      loading: GridExpanderLoader
    }),
    GridLoadingItem,
    ProfileTitle
  },
  props: {
    ...LINK_PROPS
  },
  setup(props, _ref) {
    let {
      emit
    } = _ref;
    const isLoading = ref(false);
    const gridItemRefs = ref(null);
    const selectedItem = ref(null);
    const selectedItemIndex = ref(null);
    const {
      apiBaseUrl
    } = useConfiguration();
    const items = ref(props.link.items);
    const hasMore = ref(true);
    const page = ref(1);
    const {
      isInAdmin = false
    } = inject('app', {});
    const {
      id,
      isFreePlan,
      enableUtmParameters,
      utmMedium,
      utmSource
    } = useLink(props, {
      emit
    });
    const disableMultipleUrls = computed(() => {
      return isFreePlan.value;
    });
    const gridOptions = computed(() => {
      return props.link.image_grid_options;
    });
    const itemsToShow = computed(() => {
      var _items$value;
      const filteredItems = (_items$value = items.value) === null || _items$value === void 0 ? void 0 : _items$value.filter(i => {
        var _i$urls;
        return i.enabled && ((_i$urls = i.urls) === null || _i$urls === void 0 ? void 0 : _i$urls.length);
      });
      return gridOptions.value.limit ? filteredItems === null || filteredItems === void 0 ? void 0 : filteredItems.slice(0, gridOptions.value.limit) : filteredItems;
    });
    function reset(newItems) {
      items.value = newItems;
      hasMore.value = true;
      page.value = 1;
      if (isInAdmin) load();
    }
    const resetDebounced = debounce(reset, 1000);

    // Reset component when updates are made to the link items
    // This will only happen in the admin
    watch(() => props.link.items, resetDebounced);
    const itemsCount = computed(() => {
      return props.link.items_enabled_count;
    });
    const loadingItemsCount = computed(() => {
      if (!hasMore.value) return 0;
      if (gridOptions.value.limit) return Math.min(gridOptions.value.limit, itemsCount.value) - itemsToShow.value.length;
      return itemsCount.value - itemsToShow.value.length;
    });
    const expandOrder = computed(() => {
      const normalizedIndex = selectedItemIndex.value + 1;

      // Always round up to the nearest multiple of 3
      return Math.ceil(normalizedIndex / 3) * 3 - 1;
    });
    function handleClick(e, item, i) {
      const {
        isButton,
        ...eventData
      } = e;
      if (isButton) {
        selectedItem.value = item;
        selectedItemIndex.value = i;
      } else {
        handleExpandClose();
      }
      emit('click', {
        ...eventData,
        position: i + 1
      });
    }
    function handleExpandClose() {
      selectedItem.value = null;
      // Refocus on the grid item
      if (selectedItemIndex.value >= 0 && selectedItemIndex.value) gridItemRefs.value[selectedItemIndex.value].$el.focus();
      selectedItemIndex.value = null;
    }
    async function load() {
      if (isLoading.value || !hasMore.value) return;
      isLoading.value = true;
      try {
        const {
          data
        } = await localAxios.get(`${apiBaseUrl}/public/links/${id.value}/items`, {
          params: {
            page: page.value
          }
        });
        if (page.value === 1) {
          items.value = data;
        } else {
          // Remove any items with matching ids
          const newItems = data.filter(item => items.value.find(existingItem => existingItem._id.$oid === item._id.$oid) === undefined);
          items.value = [...items.value, ...newItems];
        }
        if (data.length < PAGE_SIZE) {
          hasMore.value = false;
        }
      } catch (e) {
        // const { error_message } = (e.response && e.response.data) || {};
        console.error(e);
        page.value--;
      }
      isLoading.value = false;
    }
    function handleNextPage() {
      if (isLoading.value) return;
      page.value++;
      load();
    }
    onMounted(() => {
      // Load first page in admin, otherwise things will be off
      if (isInAdmin) load();
    });
    return {
      id,
      gridOptions,
      itemsToShow,
      itemsCount,
      handleClick,
      selectedItem,
      selectedItemIndex,
      expandOrder,
      loadingItemsCount,
      handleNextPage,
      gridItemRefs,
      handleExpandClose,
      isInAdmin,
      disableMultipleUrls,
      enableUtmParameters,
      utmMedium,
      utmSource
    };
  }
};
export { script as default };